/** @file Zod schemas for Firestore documents + domain primitives. */
import { z } from "zod";

export const positionLabelSchema = z
  .enum(["TW", "LA", "RL", "RM", "RR", "RA", "KM"])
  .nullable();
export const tokenKindSchema = z.enum(["player", "goalkeeper", "ball"]);
export const teamSideSchema = z.enum(["home", "away"]).nullable();
export const drawingTypeSchema = z.enum(["run", "pass", "shot"]);

export const pointSchema = z.object({
  x: z.number().min(0).max(40),
  y: z.number().min(0).max(20),
});

export const tokenSchema = z.object({
  id: z.string().min(1),
  kind: tokenKindSchema,
  team: teamSideSchema,
  number: z.number().int().min(0).max(99),
  label: positionLabelSchema,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  x: z.number().min(0).max(40),
  y: z.number().min(0).max(20),
});

export const drawingSchema = z.object({
  id: z.string().min(1),
  type: drawingTypeSchema,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  points: z.array(pointSchema).min(2),
});

export const rosterPlayerSchema = z.object({
  firstName: z.string().min(1).max(40),
  number: z.number().int().min(0).max(99),
  position: positionLabelSchema.optional(),
});

export const teamDocSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(60),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  roster: z.array(rosterPlayerSchema).max(25),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const sceneSchema = z.object({
  id: z.string().min(1),
  tokens: z.record(z.string().min(1), pointSchema),
  ball: pointSchema.nullable(),
  visibleArrowIds: z.array(z.string()).max(200),
  durationMs: z.number().int().min(100).max(60_000).default(2000),
});

export const playDocSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(80),
  tokens: z.array(tokenSchema).min(1).max(20),
  drawings: z.array(drawingSchema).max(200),
  scenes: z.array(sceneSchema).min(1).max(50),
  thumbnail: z.string().startsWith("data:image/").max(40_000).optional(),
  teamId: z.string().nullable().optional(),
  publicShareIds: z.array(z.string().min(10).max(24)).max(20).optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  schemaVersion: z.literal(2),
});

export const shareDocSchema = z.object({
  shareId: z.string().min(10).max(24),
  playId: z.string().min(1),
  ownerId: z.string().min(1),
  createdAt: z.date(),
  revoked: z.boolean(),
});

export const userDocSchema = z.object({
  uid: z.string().min(1),
  displayName: z.string().max(80).nullable(),
  createdAt: z.date(),
});

export const PLAY_SCHEMA_VERSION = 2;
